/**
 * Handles chunk load failures that occur when a new deployment has changed file hashes
 * Shows a notification to the user before reloading the page
 */

let hasShownNotification = false
let appUpdateStore = null

// Store reference will be set after Pinia is initialized
export const setAppUpdateStore = (store) => {
  appUpdateStore = store
}

export const isChunkLoadError = (error) => {
  const message = error?.message || ''
  return (
    message.includes('Failed to fetch dynamically imported module') ||
    message.includes('Importing a module script failed') ||
    message.includes('error loading dynamically imported module') ||
    message.includes('is not a valid JavaScript MIME type') ||
    (error instanceof TypeError && message.includes('fetch'))
  )
}

export const handleChunkLoadError = (error) => {
  if (!isChunkLoadError(error)) {
    return false
  }

  console.warn('Chunk load failure detected - app has been updated', error)

  // If user previously dismissed an update and now we're getting chunk load errors,
  // show the update warning again as dismissing the update caused problems
  if (appUpdateStore?.updateAvailable && appUpdateStore?.hasShownWarning) {
    console.error('Chunk load error after user dismissed update - re-triggering update warning')
    if (!hasShownNotification) {
      hasShownNotification = true
      appUpdateStore.showUpdateWarningIfDismissed()
    }
    return true
  }

  // Otherwise, show initial update notification if not already shown
  if (hasShownNotification) {
    return false
  }

  hasShownNotification = true

  // Use the store if available (after Vue/Pinia initialization)
  if (appUpdateStore) {
    appUpdateStore.triggerUpdate()
  } else {
    // Fallback to browser confirm if store not available yet
    const shouldReload = confirm(
      'A new version of the application is available. The page will reload to get the latest version.\n\nClick OK to reload now.'
    )

    if (shouldReload) {
      window.location.reload()
    } else {
      // User declined, but warn them that the app may not work correctly
      console.warn('User declined reload after chunk load failure - app may be unstable')
      // Reset flag after a delay in case they try again
      setTimeout(() => {
        hasShownNotification = false
      }, 5000)
    }
  }

  return true
}

export const initChunkLoadHandler = () => {
  // Global unhandled rejection handler
  window.addEventListener('unhandledrejection', (event) => {
    if (handleChunkLoadError(event.reason)) {
      event.preventDefault()
    }
  })

  // Global error handler as backup
  window.addEventListener('error', (event) => {
    if (event.error && handleChunkLoadError(event.error)) {
      event.preventDefault()
    }
  })
}
